import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, ArrowUpRight, Pencil, Lock, X, LogOut } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "@/data/products";
import {
  fetchCustomItems,
  createItem,
  updateItem,
  deleteItem,
  loginAdmin,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
} from "@/lib/api";

const EMPTY = {
  name: "",
  category: "Household",
  rating: "",
  ratings_count: "",
  amazon_url: "",
  image: "",
  takeaway: "",
};

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-parchment/50 mb-2">
      {label}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-transparent border border-parchment/20 px-4 py-3 text-sm text-parchment placeholder:text-parchment/30 focus:outline-none focus:border-brass transition-colors";

const LockScreen = ({ onUnlock }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { token } = await loginAdmin(password);
      setAdminToken(token);
      onUnlock(token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not unlock. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="manage-lock-screen" className="min-h-screen bg-charcoal text-parchment flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm border border-parchment/15 p-10"
      >
        <Lock size={20} className="text-brass mb-6" />
        <p className="font-serif text-2xl tracking-tight">
          Findora <span className="text-brass">|</span> <span className="italic">Owner access</span>
        </p>
        <p className="mt-3 text-sm text-parchment/50 leading-relaxed">
          This page is private. Enter the owner password to manage the catalogue.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            data-testid="manage-password-input"
            type="password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoFocus
          />
          {error && (
            <p data-testid="manage-unlock-error" className="font-mono text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="manage-unlock-submit"
            type="submit"
            disabled={busy || !password}
            className="w-full font-mono text-[11px] uppercase tracking-[0.25em] border border-brass text-brass px-6 py-3.5 hover:bg-brass hover:text-charcoal transition-colors duration-300 disabled:opacity-50"
          >
            {busy ? "Unlocking…" : "Unlock"}
          </button>
        </form>
        <Link
          data-testid="manage-lock-back-link"
          to="/"
          className="mt-6 inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-parchment/40 hover:text-brass transition-colors"
        >
          ← Back to site
        </Link>
      </motion.div>
    </div>
  );
};

export default function ManagePage() {
  const [token, setToken] = useState(getAdminToken());
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Manage Catalogue — Findora";
    if (token) fetchCustomItems().then(setItems).catch(() => {});
  }, [token]);

  if (!token) return <LockScreen onUnlock={setToken} />;

  const handleExpired = () => {
    clearAdminToken();
    setToken(null);
    toast.error("Session expired — unlock again.");
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      category: item.category,
      rating: item.rating ?? "",
      ratings_count: item.ratings_count ?? "",
      amazon_url: item.amazon_url,
      image: item.image ?? "",
      takeaway: item.takeaway ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.amazon_url.trim()) {
      toast.error("Name and Amazon link are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        amazon_url: form.amazon_url.trim(),
        image: form.image.trim() || null,
        rating: form.rating ? Number(form.rating) : null,
        ratings_count: form.ratings_count ? Number(form.ratings_count) : null,
        takeaway: form.takeaway.trim() || null,
      };
      if (editingId) {
        const updated = await updateItem(editingId, payload);
        setItems((prev) => prev.map((i) => (i.id === editingId ? updated : i)));
        toast.success(`${updated.code} updated — changes are live.`);
      } else {
        const created = await createItem(payload);
        setItems((prev) => [...prev, created]);
        toast.success(`${created.code} added — it's live on the catalog now.`);
      }
      cancelEdit();
    } catch (err) {
      if (err.response?.status === 401) return handleExpired();
      toast.error("Could not save the item. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    try {
      await deleteItem(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      if (editingId === item.id) cancelEdit();
      toast.success(`${item.code} removed from the catalog.`);
    } catch (err) {
      if (err.response?.status === 401) return handleExpired();
      toast.error("Could not delete the item.");
    }
  };

  const logout = () => {
    clearAdminToken();
    setToken(null);
  };

  return (
    <div data-testid="manage-page" className="min-h-screen bg-charcoal text-parchment">
      <header className="border-b border-parchment/10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-serif text-lg tracking-tight">
            Findora <span className="text-brass">|</span>{" "}
            <span className="italic">Catalogue Manager</span>
          </span>
          <div className="flex items-center gap-6">
            <Link
              data-testid="manage-back-link"
              to="/"
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-parchment/50 hover:text-brass transition-colors"
            >
              ← Back to site
            </Link>
            <button
              data-testid="manage-lock-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-parchment/50 hover:text-brass transition-colors"
            >
              <LogOut size={12} /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brass mb-3">
            Owner only — unlisted page
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight" data-testid="manage-form-heading">
            {editingId ? "Edit this pick" : "Add a new pick"}
          </h1>
          <p className="mt-3 text-sm text-parchment/60 leading-relaxed">
            {editingId
              ? "Change any detail and save — the public catalog updates instantly."
              : "Anything you add here appears instantly in the public catalog under its category. Only name, category and the Amazon link are required."}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="admin-manage-form">
            <Field label="Product name *" testId="admin-manage-field-name">
              <input
                data-testid="admin-manage-name-input"
                className={inputCls}
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Cast Iron Skillet, 12-inch"
              />
            </Field>

            <Field label="Category *" testId="admin-manage-field-category">
              <select
                data-testid="admin-manage-category-select"
                className={`${inputCls} bg-charcoal`}
                value={form.category}
                onChange={set("category")}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Amazon link *" testId="admin-manage-field-amazon-url">
              <input
                data-testid="admin-manage-amazon-url-input"
                className={inputCls}
                value={form.amazon_url}
                onChange={set("amazon_url")}
                placeholder="https://www.amazon.com/..."
              />
            </Field>

            <Field label="Photo link (optional)" testId="admin-manage-field-image">
              <input
                data-testid="admin-manage-image-input"
                className={inputCls}
                value={form.image}
                onChange={set("image")}
                placeholder="https://… direct link to the product photo"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Rating (1–5)" testId="admin-manage-field-rating">
                <input
                  data-testid="admin-manage-rating-input"
                  className={inputCls}
                  type="number"
                  step="0.1"
                  min="1"
                  max="5"
                  value={form.rating}
                  onChange={set("rating")}
                  placeholder="4.6"
                />
              </Field>
              <Field label="Ratings count" testId="admin-manage-field-ratings-count">
                <input
                  data-testid="admin-manage-ratings-count-input"
                  className={inputCls}
                  type="number"
                  min="0"
                  value={form.ratings_count}
                  onChange={set("ratings_count")}
                  placeholder="1,240"
                />
              </Field>
            </div>

            <Field label="Your takeaway (optional)" testId="admin-manage-field-takeaway">
              <textarea
                data-testid="admin-manage-takeaway-input"
                className={`${inputCls} min-h-[90px] resize-y`}
                value={form.takeaway}
                onChange={set("takeaway")}
                placeholder="One honest line on why it made the cut."
              />
            </Field>

            <div className="flex items-center gap-4">
              <button
                data-testid="admin-add-item-submit"
                type="submit"
                disabled={saving}
                className="font-mono text-[11px] uppercase tracking-[0.25em] border border-brass text-brass px-6 py-3.5 hover:bg-brass hover:text-charcoal transition-colors duration-300 disabled:opacity-50"
              >
                {saving ? "Saving…" : editingId ? "Save changes" : "Add to catalog"}
              </button>
              {editingId && (
                <button
                  data-testid="admin-edit-cancel"
                  type="button"
                  onClick={cancelEdit}
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-parchment/50 hover:text-parchment transition-colors"
                >
                  <X size={12} /> Cancel
                </button>
              )}
            </div>
          </form>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <h2 className="font-serif text-2xl tracking-tight mb-2">
            Your added items
          </h2>
          <p className="text-sm text-parchment/50 mb-8">
            {items.length === 0
              ? "None yet — items you add will show up here."
              : `${items.length} custom item${items.length === 1 ? "" : "s"} live on the site.`}
          </p>

          <div className="space-y-4" data-testid="admin-manage-item-list">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`admin-item-row-${item.code}`}
                className={`border p-5 flex items-start justify-between gap-4 transition-colors ${
                  editingId === item.id ? "border-brass" : "border-parchment/15"
                }`}
              >
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-brass border border-brass/30 px-2 py-0.5">
                      {item.code}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-parchment/40">
                      {item.category}
                    </span>
                  </div>
                  <p className="mt-2 font-serif text-lg leading-snug">{item.name}</p>
                  <a
                    data-testid={`admin-item-link-${item.code}`}
                    href={item.amazon_url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-parchment/40 hover:text-brass transition-colors"
                  >
                    Amazon link <ArrowUpRight size={11} />
                  </a>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`admin-item-edit-${item.code}`}
                    onClick={() => startEdit(item)}
                    className="text-parchment/40 hover:text-brass transition-colors p-1"
                    aria-label={`Edit ${item.name}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`admin-item-delete-${item.code}`}
                    onClick={() => remove(item)}
                    className="text-parchment/40 hover:text-red-400 transition-colors p-1"
                    aria-label={`Delete ${item.name}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.section>
      </main>
    </div>
  );
}
